/** @format */

import React, { useMemo } from "react";
import ReactFlow, {
    Background,
    Controls,
    Edge,
    MarkerType,
    Node,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box, CircularProgress, Typography } from "@mui/material";

import {
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionSubtitle,
    CollectSectionTitle,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import {
    IDashboardOverview,
    IDashboardStageBucket,
} from "../../../utility/types/collectTypes";

interface Props {
    overview: IDashboardOverview | null;
    isLoading: boolean;
}

interface StageDef {
    id: string;
    label: string;
    x: number;
    y: number;
    tone: "neutral" | "ok" | "warn" | "danger" | "info";
}

// Linear pipeline left → right, with PTP/Disputed/Manual_Review as a
// branching row beneath active_recovery.
const STAGES: StageDef[] = [
    { id: "monitoring", label: "Monitoring", x: 0, y: 80, tone: "neutral" },
    { id: "due_soon", label: "Due Soon", x: 160, y: 80, tone: "info" },
    { id: "due_today", label: "Due Today", x: 320, y: 80, tone: "info" },
    { id: "overdue", label: "Overdue", x: 480, y: 80, tone: "warn" },
    { id: "active_recovery", label: "Active Recovery", x: 660, y: 80, tone: "warn" },
    { id: "promise_to_pay", label: "Promise to Pay", x: 540, y: 200, tone: "info" },
    { id: "partially_paid", label: "Partially Paid", x: 700, y: 200, tone: "info" },
    { id: "disputed", label: "Disputed", x: 540, y: 0, tone: "danger" },
    { id: "manual_review", label: "Manual Review", x: 700, y: 0, tone: "danger" },
    { id: "recovered", label: "Recovered", x: 880, y: 80, tone: "ok" },
    { id: "closed", label: "Closed", x: 1040, y: 80, tone: "neutral" },
];

const TONE_STYLES: Record<
    StageDef["tone"],
    { bg: string; border: string; fg: string; activeBg: string; activeBorder: string }
> = {
    neutral: {
        bg: "#f9fafb",
        border: "#e5e7eb",
        fg: "#374151",
        activeBg: "#f3f4f6",
        activeBorder: "#9ca3af",
    },
    ok: {
        bg: "#f0fdf4",
        border: "#bbf7d0",
        fg: "#166534",
        activeBg: "#dcfce7",
        activeBorder: "#22c55e",
    },
    info: {
        bg: "#eff6ff",
        border: "#bfdbfe",
        fg: "#1e40af",
        activeBg: "#dbeafe",
        activeBorder: "#3b82f6",
    },
    warn: {
        bg: "#fffbeb",
        border: "#fde68a",
        fg: "#92400e",
        activeBg: "#fef3c7",
        activeBorder: "#f59e0b",
    },
    danger: {
        bg: "#fef2f2",
        border: "#fecaca",
        fg: "#991b1b",
        activeBg: "#fee2e2",
        activeBorder: "#ef4444",
    },
};

const EDGES: Array<{ src: string; dst: string; dashed?: boolean }> = [
    { src: "monitoring", dst: "due_soon" },
    { src: "due_soon", dst: "due_today" },
    { src: "due_today", dst: "overdue" },
    { src: "overdue", dst: "active_recovery" },
    { src: "active_recovery", dst: "recovered" },
    { src: "recovered", dst: "closed" },
    { src: "active_recovery", dst: "promise_to_pay", dashed: true },
    { src: "active_recovery", dst: "partially_paid", dashed: true },
    { src: "active_recovery", dst: "disputed", dashed: true },
    { src: "active_recovery", dst: "manual_review", dashed: true },
    { src: "promise_to_pay", dst: "recovered", dashed: true },
    { src: "partially_paid", dst: "recovered", dashed: true },
];

function fmtCurrency(amount: number, currency = "USD"): string {
    if (!amount) return "$0";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `$${amount.toFixed(0)}`;
    }
}

const WorkflowFlowDiagram: React.FC<Props> = ({ overview, isLoading }) => {
    const stages = overview?.workflowStages ?? {};

    const { nodes, edges } = useMemo(() => {
        const nodeList: Node[] = STAGES.map((s) => {
            const bucket: IDashboardStageBucket = stages[s.id] || {
                count: 0,
                totalBalance: 0,
            };
            const active = bucket.count > 0;
            const tone = TONE_STYLES[s.tone];
            return {
                id: s.id,
                position: { x: s.x, y: s.y },
                draggable: false,
                connectable: false,
                data: {
                    label: (
                        <Box
                            sx={{
                                padding: "8px 10px",
                                minWidth: 130,
                                textAlign: "left",
                                fontFamily: "'Montserrat', sans-serif",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    color: active ? tone.fg : "#6b7280",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    lineHeight: 1.2,
                                }}
                            >
                                {s.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "1.1rem",
                                    fontWeight: 700,
                                    color: active ? tone.fg : "#9ca3af",
                                    marginTop: "2px",
                                }}
                            >
                                {bucket.count}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    color: active ? tone.fg : "#9ca3af",
                                    marginTop: "1px",
                                }}
                            >
                                {fmtCurrency(bucket.totalBalance)}
                            </Typography>
                        </Box>
                    ),
                },
                style: {
                    background: active ? tone.activeBg : tone.bg,
                    border: `${active ? "2px" : "1px"} solid ${active ? tone.activeBorder : tone.border}`,
                    borderRadius: "10px",
                    padding: 0,
                    boxShadow: active ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                },
            };
        });

        const edgeList: Edge[] = EDGES.map((e) => {
            const srcActive = (stages[e.src]?.count || 0) > 0;
            return {
                id: `${e.src}->${e.dst}`,
                source: e.src,
                target: e.dst,
                animated: srcActive && !e.dashed,
                style: {
                    stroke: srcActive ? "#6b7280" : "#d1d5db",
                    strokeWidth: srcActive ? 1.6 : 1,
                    strokeDasharray: e.dashed ? "4 3" : undefined,
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: srcActive ? "#6b7280" : "#d1d5db",
                },
            };
        });

        return { nodes: nodeList, edges: edgeList };
    }, [stages]);

    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>Recovery Workflow</CollectSectionTitle>
                    <CollectSectionSubtitle>
                        Live count of invoices at each stage of your collections
                        pipeline. Highlighted nodes have invoices in them right now.
                    </CollectSectionSubtitle>
                </Box>
            </CollectSectionHeader>

            {isLoading && !overview ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        height: 360,
                        borderRadius: "8px",
                        backgroundColor: "#fafafa",
                        border: "1px solid #f3f4f6",
                    }}
                >
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        fitView
                        fitViewOptions={{ padding: 0.15 }}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        zoomOnScroll={false}
                        panOnScroll={false}
                        proOptions={{ hideAttribution: true }}
                    >
                        <Background gap={20} size={1} color="#e5e7eb" />
                        <Controls
                            showInteractive={false}
                            position="bottom-right"
                            style={{ scale: "0.8" }}
                        />
                    </ReactFlow>
                </Box>
            )}
        </CollectSectionCard>
    );
};

export default WorkflowFlowDiagram;
